/**
 * useOrders Hook
 *
 * Fetches list of user's orders using URQL.
 */

import { useQuery } from "urql";
import type { Order } from "@/generated/graphql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const OrdersDocument = `
  query Orders {
    orders {
      id
      status
      totalAmount
      currency
      createdAt
      event {
        id
        title
        startsAt
        imageUrl
      }
      items {
        quantity
        ticketType {
          name
        }
      }
    }
  }
`;

// =============================================================================
// HOOK
// =============================================================================

interface UseOrdersOptions {
  /** Whether to pause the query */
  pause?: boolean;
}

interface UseOrdersResult {
  orders: Order[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch list of user's orders.
 *
 * Uses URQL for normalized caching. After mutations like createOrder,
 * the cache automatically invalidates and refetches.
 */
export function useOrders(options: UseOrdersOptions = {}): UseOrdersResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{ orders: Order[] }>({
    query: OrdersDocument,
    pause,
  });

  return {
    orders: result.data?.orders ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
